import { useCallback, useEffect, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";
import type { PlatformTimerSummary } from "@tdelivr/api-client/generated/workflow/delayed-command-timer.js";
import type { PlatformScheduleSummary } from "@tdelivr/api-client/generated/workflow/scheduler.js";
import type { PlatformWorkflowSummary } from "@tdelivr/api-client/generated/workflow/workflow-orchestrator.js";

import { PER_FETCH } from "../board.ts";
import type { Session } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import {
  DELAYED_TIMERS_SCOPE,
  SCHEDULES_SCOPE,
  SCHEDULE_REFUSAL,
  WORKFLOWS_SCOPE,
  listingNote,
  statesPresent,
  workflowRows,
  type WorkflowListings,
} from "../workflow-schedules.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * `workflow` in `16 WP15 Platform Machinery.dc.html`: workflows, schedules and delayed timers.
 *
 * ```
 * workflow:['Workflow and schedules','WRK-01…03',
 *   'Orchestrated workflows, timers and scheduled jobs.',
 *   [['Running','128','active','workflows'],['Stuck','2','human','past deadline'],
 *    ['Timers due','14','waiting','next 5 min'],['Failed today','1','failed','']],
 *   ['Workflow','Step','Due'],
 *   [['wf_payout_0928','await approval','—','human','waiting on a person'],
 *    ['wf_tenant_5HP2','provision search index','now','active','running'],
 *    ['wf_expire_docs','nightly 23:59','in 9 h','waiting','scheduled']],
 *   'info','Stuck workflows show the step they are waiting on and who can unblock them.']
 * ```
 *
 * What is drawn from data and what is not:
 *
 * | drawn                                   | source                                                   |
 * | --------------------------------------- | -------------------------------------------------------- |
 * | the rows, three record kinds in one grid | WRK-01, WRK-02 and WRK-03's platform listings            |
 * | `Workflow` — `wf_payout_0928`           | `definitionName`, `triggerKey`, `commandType`. Q-219      |
 * | `Step` — `await approval`               | WRK-01's `currentActivityType`, verbatim. Q-222           |
 * | `Step` — `nightly 23:59`                | WRK-02's `cronExpression`, **as published**. Q-219        |
 * | `Step` for a delayed command            | **nothing** — WRK-03 models no step                       |
 * | `Due` — `in 9 h`, `now`                 | `nextTimerFireAt`, `nextFireAt`, `dueAt`                  |
 * | `active · running`, `waiting · scheduled` | each service's own `status`                             |
 * | `human · waiting on a person`           | **nothing** — no deadline is modelled. Q-218              |
 * | the four KPI tiles                      | **nothing** — R-04 and Q-178; `Stuck` is Q-218 as well    |
 * | `Schedule job`                          | drawn and refused — `SCHEDULE_REFUSAL`, ADRs 0061–0062    |
 *
 * ## Three services, three scopes, one grid — and only one of them is the board's failure
 *
 * ADR 0061 §7 decides that the three listings are read separately rather than composed behind
 * EXP-005, and names the cost: one toolbar and one footer over three independent cursors. **Q-223**
 * is that question and its meanwhile is what this does — each listing is asked for its own full
 * first page and the three are merged.
 *
 * The three are not equal, and the asymmetry is `ui/EventCatalogue.tsx`'s:
 *
 *   - **WRK-01 is the grid.** The page is titled for workflows, its first two designed rows are
 *     executions, and a failure there is the board's failure state.
 *   - **WRK-02 and WRK-03 are rows that may be missing.** A failure in either is caught, those rows
 *     do not appear, and a sentence under the grid says which service did not answer.
 *
 * One `Promise.all` would hide every workflow on the platform because a second service was
 * unreachable, on the page an operator opens *because* something is stuck. That is the whole of the
 * argument and it is the reason the three awaits below are not one.
 *
 * ## What a platform-scoped read did not give this page
 *
 * **The deadline, and with it the entire "needs a person" apparatus.** The `Stuck · 2 · past
 * deadline` tile, the `human` row tint, the `waiting on a person` badge and the detail panel's
 * `Open approval` are one absence in four shapes, and nothing on this platform models it — ADR 0061
 * §Context measured it and ADR 0061 §8 declines to invent it. None of the four is drawn. The panel
 * draws no `Open approval` as a consequence rather than as a special case: `ui/RowDetail.tsx` keys
 * its action off the state class, and no row here can be `human`.
 *
 * Two smaller ones travel with it, both recorded: the `Step` column asks for a human phrase where
 * WRK-01 holds an activity type (**Q-222**), and the first column is a slug where every identifier
 * behind it is a UUID (**Q-219**). `../workflow-schedules.ts` argues each cell.
 */

const workflowOrchestrator = services.workflow__workflow_orchestrator;
const scheduler = services.workflow__scheduler;
const delayedTimers = services.workflow__delayed_command_timer;

export interface WorkflowSchedulesProps {
  readonly transport: Transport;
  readonly session: Session;
  /** Innermost last: `Platform › Machinery › Workflow and schedules`, the design's trail. */
  readonly crumbs: readonly string[];
}

/** The design's `PAGES` row, so the words on screen and the words here cannot drift apart. */
const TITLE = "Workflow and schedules";
/** The design's own source chip, word for word, ellipsis included: `WRK-01…03`. */
const SOURCE = "WRK-01…03";
const DESCRIPTION = "Orchestrated workflows, timers and scheduled jobs.";
const COLUMNS = ["Workflow", "Step", "Due"] as const;
const NOTE = "Stuck workflows show the step they are waiting on and who can unblock them.";
/** The design's own `noteK` for this page is `info`, which its `NK` map draws in the sky tint. */
const NOTE_KIND = "info" as const;
/** The design's `FORMS.workflow[0]`, which is the word on its heading button. */
const PRIMARY_LABEL = "Schedule job";

export function WorkflowSchedules({
  transport,
  session,
  crumbs,
}: WorkflowSchedulesProps): ReactNode {
  const [workflows, setWorkflows] = useState<readonly PlatformWorkflowSummary[] | undefined>(
    undefined,
  );
  const [schedules, setSchedules] = useState<readonly PlatformScheduleSummary[] | undefined>(
    undefined,
  );
  const [timers, setTimers] = useState<readonly PlatformTimerSummary[] | undefined>(undefined);
  const [truncated, setTruncated] = useState<readonly string[]>([]);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");
  /**
   * The instant every `Due` cell is measured against, pinned when the three listings answered.
   *
   * Pinned rather than read per render: a `Date.now()` inside a cell makes the same row re-render
   * to a different word for no reason the operator caused, and makes a fidelity capture
   * unrepeatable. Pressing retry re-reads it, which is the control that means "where is it now".
   */
  const [measuredAt, setMeasuredAt] = useState(() => Date.now());

  const load = useCallback(async () => {
    setWorkflows(undefined);
    setSchedules(undefined);
    setTimers(undefined);
    setFailure(undefined);

    /*
     * All three scopes asked for before any call, not discovered from a refusal.
     *
     * One state naming whichever are missing, because that is what an operator can act on: they can
     * ask their administrator for the scopes by name. A 403 from any service renders the same
     * state, so this is not the only guard; it is the one that makes all three literals appear in
     * this application's source, which is what `channel-scopes.mjs` derives the Keycloak grant
     * from. ADR 0049 §3.
     *
     * **This is the page ADR 0061 §7's fourth reason is about**: one screen spending three
     * cross-tenant scopes at once, which is Q-139 multiplied and which nobody has decided.
     */
    const missing = [WORKFLOWS_SCOPE, SCHEDULES_SCOPE, DELAYED_TIMERS_SCOPE].filter(
      (scope) => !session.scopes.has(scope),
    );
    if (missing.length > 0) {
      setFailure({ kind: "forbidden", missingScopes: missing });
      return;
    }

    /*
     * The three platform listings, not the nineteen internal operations behind them.
     *
     * WRK-01's ten, WRK-02's three and WRK-03's six are all `/internal/v1/tenants/{tenantId}/…`,
     * each refused 403 when the verified context names a different tenant, so there is no wrong one
     * to call by accident. WRK-01's `getWorkflow` is the one worth naming anyway: it answers the
     * whole aggregate including the `history[]` this listing withholds.
     *
     * Started together and awaited apart, which is this file's header.
     */
    const workflowPage = workflowOrchestrator
      .createWorkflowOrchestratorClient(transport)
      .listPlatformWorkflows(PER_FETCH);
    const schedulePage = scheduler
      .createSchedulerClient(transport)
      .listPlatformSchedules(PER_FETCH);
    const timerPage = delayedTimers
      .createDelayedCommandTimerClient(transport)
      .listPlatformDelayedTimers(PER_FETCH);

    /*
     * Both secondaries' rejections are caught here and not left for the `await`s below, because an
     * unhandled rejection on a promise started before an earlier `await` threw is an unhandled
     * rejection warning rather than a handled failure.
     */
    const scheduled = schedulePage.then(
      (page) => page,
      () => undefined,
    );
    const delayed = timerPage.then(
      (page) => page,
      () => undefined,
    );

    setMeasuredAt(Date.now());

    const bounded: string[] = [];
    try {
      const page = await workflowPage;
      setWorkflows(page.workflows);
      if (page.nextAfter !== null) bounded.push("WRK-01's workflows");
    } catch (error) {
      setFailure(stateForError(error, [WORKFLOWS_SCOPE]));
      return;
    }

    const schedulesPage = await scheduled;
    setSchedules(schedulesPage?.schedules);
    if (schedulesPage !== undefined && schedulesPage.nextAfter !== null) {
      bounded.push("WRK-02's schedules");
    }

    const timersPage = await delayed;
    setTimers(timersPage?.timers);
    if (timersPage !== undefined && timersPage.nextAfter !== null) {
      bounded.push("WRK-03's delayed timers");
    }

    setTruncated(bounded);
  }, [transport, session.scopes]);

  useEffect(() => {
    void load();
  }, [load]);

  const listings: WorkflowListings = { workflows, schedules, timers };
  const rows = workflows === undefined ? undefined : workflowRows(listings, measuredAt);
  const shown = rows?.filter((row) => state === "" || row.state.label === state);

  /*
   * `boundedNote` is not used, and the reason is Q-223.
   *
   * It writes one sentence about one listing's `nextAfter` and one ordering. This grid is three
   * listings with three cursors and three orderings, so one of its sentences would be true of a
   * third of the rows and silently false of the rest. The sentence this board needs names which of
   * the three was cut, which is what `truncated` collects.
   */
  const bounded =
    truncated.length === 0
      ? undefined
      : `More than ${PER_FETCH} rows match in ${truncated.join(" and ")}. This board shows each listing's first page, ordered by tenant and then by the record's own identifier, and merges them — so the count is a floor rather than a total and one listing can be shown further than another. Q-223.`;
  const footnote = rows === undefined ? undefined : listingNote(listings);

  return (
    <RecordsBoard
      crumbs={crumbs}
      title={TITLE}
      source={SOURCE}
      description={DESCRIPTION}
      columns={COLUMNS}
      rows={shown}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="No workflow, schedule or delayed command exists on any tenant. A row appears here when a workflow is started, a schedule is created or a command is deferred."
      primary={{ label: PRIMARY_LABEL, refusal: SCHEDULE_REFUSAL }}
      filter={{
        // The design's own word for unfiltered, from its `gridUI`: `All states`.
        unfiltered: "All states",
        options: (rows === undefined ? [] : statesPresent(rows)).map((label) => ({
          value: label,
          label,
        })),
        value: state,
        onChange: setState,
      }}
      note={{ kind: NOTE_KIND, glyph: "info", text: NOTE }}
      {...(bounded === undefined ? {} : { bounded })}
      {...(footnote === undefined ? {} : { footnote })}
      exportName="workflow-and-schedules"
      detailSubtitle={`${TITLE} · ${SOURCE}`}
      detailNote={NOTE}
    />
  );
}
